import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Monitor, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { fleet } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { useMqtt } from '../hooks/useMqtt';
import { StatusDot } from '../components/StatusDot';
import { MetricCard } from '../components/MetricCard';
import { TableSkeleton } from '../components/Skeleton';
import type { Device, DeviceStatus } from '../types';

export default function FleetPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: devices = [], isLoading, error } = useQuery({
    queryKey: ['fleet', 'devices'],
    queryFn: fleet.listDevices,
    refetchInterval: 30_000,
  });

  // Real-time MQTT telemetry overlay (viewport-scoped for large fleets).
  // Must be called before any conditional return to satisfy the Rules of Hooks
  // — skipping this hook on error renders would cause "Rendered fewer hooks
  // than expected" (React error #300).
  const { isConnected, telemetry, observeRow } = useMqtt(devices);

  const rowRefs = useMemo(
    () => new Map(devices.map((a) => [a.serial, observeRow(a.serial)])),
    [devices.map(a => a.serial).join(','), observeRow],
  );

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400 mb-2">Failed to load fleet data</p>
        <p className="text-xs text-gray-500">{(error as Error).message}</p>
        <button
          onClick={() => window.location.reload()}
          className="btn-primary mt-4 text-sm"
        >
          Reload Page
        </button>
      </div>
    );
  }

  const statusCounts = devices.reduce(
    (acc, device) => {
      acc[device.status] = (acc[device.status] || 0) + 1;
      return acc;
    },
    {} as Record<DeviceStatus, number>,
  );

  const pageTitle =
    user?.org_type === 'kinetica'
      ? 'Global Fleet'
      : user?.org_type === 'integrator'
        ? 'Client Fleet'
        : 'My Fleet';

  return (
    <div>
      <h2 className="text-lg font-semibold text-white mb-4">{pageTitle}</h2>

      {/* MQTT status */}
      {devices.length > 0 && (
        <div className="flex items-center gap-1.5 mb-4 text-xs">
          {isConnected ? (
            <>
              <Wifi size={12} className="text-emerald-400" />
              <span className="text-emerald-400">Live telemetry</span>
            </>
          ) : (
            <>
              <WifiOff size={12} className="text-gray-600" />
              <span className="text-gray-600">Connecting...</span>
            </>
          )}
        </div>
      )}

      {/* Metrics row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard
          label="Total Devices"
          value={devices.length}
          sub={`${statusCounts.online ?? 0} online`}
        />
        <MetricCard
          label="Online"
          value={statusCounts.online ?? 0}
          accent="text-emerald-400"
        />
        <MetricCard
          label="Faulted"
          value={statusCounts.faulted ?? 0}
          accent={statusCounts.faulted ? 'text-red-400' : 'text-gray-500'}
        />
        <MetricCard
          label="Idle"
          value={statusCounts.idle ?? 0}
          accent="text-amber-400"
        />
      </div>

      {/* Devices table */}
      <div className="card overflow-hidden p-0 overflow-x-auto">
        {isLoading ? (
          <table className="w-full">
            <tbody>
              <TableSkeleton columns={10} rows={6} />
            </tbody>
          </table>
        ) : devices.length === 0 ? (
          <div className="p-8 text-center">
            <Monitor className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">No devices found</p>
            <p className="text-xs text-gray-600 mt-1">
              {user?.org_type === 'client'
                ? 'Contact your integrator to commission devices.'
                : 'Commission devices to organisations to see them here.'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Status</th>
                <th className="table-header table-cell text-left">Serial</th>
                <th className="table-header table-cell text-left">Name</th>
                {user?.org_type !== 'client' && (
                  <th className="table-header table-cell text-left">Organisation</th>
                )}
                <th className="table-header table-cell text-left">Location</th>
                <th className="table-header table-cell text-right">Cycles</th>
                <th className="table-header table-cell text-right hidden md:table-cell">Uptime</th>
                <th className="table-header table-cell text-left">Firmware</th>
                <th className="table-header table-cell text-right hidden lg:table-cell">Cycle Time</th>
                <th className="table-header table-cell text-right hidden lg:table-cell">Power</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((device: Device) => {
                const live = telemetry.get(device.serial);
                return (
                  <tr
                    key={device.serial}
                    ref={rowRefs.get(device.serial)}
                    tabIndex={0}
                    role="link"
                    aria-label={`View details for ${device.name}`}
                    onClick={() => navigate(`/fleet/${device.serial}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/fleet/${device.serial}`);
                      }
                    }}
                    className="hover:bg-surface-2/50 transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-kinetica-600/50"
                  >
                    <td className="table-cell">
                      <StatusDot status={device.status} showLabel />
                    </td>
                    <td className="table-cell font-mono text-xs text-kinetica-400">{device.serial}</td>
                    <td className="table-cell text-gray-200">{device.name}</td>
                    {user?.org_type !== 'client' && (
                      <td className="table-cell text-gray-400 text-xs">{device.org_name}</td>
                    )}
                    <td className="table-cell text-gray-400 text-xs">{device.location}</td>
                    <td className="table-cell text-right font-mono text-xs">
                      {(device.cycle_count ?? 0).toLocaleString()}
                    </td>
                    <td className="table-cell text-right text-xs text-gray-400 hidden md:table-cell">
                      {(device.uptime_hours ?? 0).toLocaleString()}h
                    </td>
                    <td className="table-cell font-mono text-xs text-gray-500">
                      v{device.firmware_version}
                    </td>
                    <td className="table-cell text-right font-mono text-xs text-gray-400 hidden lg:table-cell">
                      {live ? `${live.cycle_time_ms}ms` : '—'}
                    </td>
                    <td className="table-cell text-right font-mono text-xs text-gray-400 hidden lg:table-cell">
                      {live ? `${live.power_watts}W` : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Safety reminder */}
      <div className="mt-4 flex items-start gap-2 text-[11px] text-gray-600">
        <AlertTriangle size={12} className="mt-0.5 flex-shrink-0" />
        <span>
          E-stop, fault propagation, and critical alerts are always active regardless of billing or
          connection status.
        </span>
      </div>
    </div>
  );
}
